import type {
  Making,
  AccountAccess,
  AdventureChoice,
  CatchUp,
  AccountPatchRequest,
  BookAccessRequest,
  BookInfo,
  CharacterDetail,
  CharacterDraftRequest,
  CharacterSummary,
  Command,
  CreatureList,
  CreatureListRequest,
  EquipmentItem,
  FeatureInfo,
  GameSummary,
  GameWire,
  GatheringView,
  InviteInfo,
  NewInviteRequest,
  JoinGameRequest,
  LibrarySearch,
  Me,
  NewAccountRequest,
  NewGameRequest,
  NewReportRequest,
  PortraitInfo,
  PortraitList,
  PortraitRequestInput,
  ConductTicket,
  ProblemReport,
  ReportPatchRequest,
  ReportStatus,
  RemovedGame,
  RulesInfo,
  SheetView,
  TableInvite,
  VaultEntry,
  VaultNote,
  SpellChoices,
  SpellNote,
  StatBlock,
  Snapshot,
  StartGameRequest,
  TrackRequest,
  AtlasWorld,
} from "@ai-rpg/protocol";

/**
 * The table whose books are being read just now, where a character is being made at someone's table:
 * "table:<id>:<code>" or "game:<id>:<code>". The server checks the word and reads the host's books.
 */
let lentBy: string | null = null;
/** Read the books of a table one has been asked to (null: one's own again). */
export function lendFrom(table: string | null): void {
  lentBy = table;
}
/** Every request, with the table whose books it reads said on it where there is one. */
const call = (path: string, init?: RequestInit): Promise<Response> =>
  fetch(
    path,
    lentBy ? { ...init, headers: { ...(init?.headers as Record<string, string>), "x-table": lentBy } } : init,
  ).catch(() => {
    // Nothing came back at all: the network, not the game. Said in words a player can act on.
    throw new Error(UNREACHED);
  });

/** What a player is told when a request never reached the server. */
export const UNREACHED = "The server couldn't be reached. Check your connection and try again: nothing was lost.";

async function problemOf(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error) return body.error;
  } catch {
    // Not JSON: fall through to the status line.
  }
  return `The server answered ${res.status}.`;
}

/** Sent on the window when the server says nobody is signed in: the session ran out, or was ended elsewhere. */
export const SIGNED_OUT = "table:signed-out";

/** The answer, or what the server said was wrong with the request. */
async function answered(res: Response): Promise<Response> {
  if (res.ok) return res;
  if (res.status === 401 && !res.url.endsWith("/api/login")) window.dispatchEvent(new Event(SIGNED_OUT));
  throw new Error(await problemOf(res));
}

async function json<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await answered(await call(path, init));
  return (await res.json()) as T;
}

const send = (method: "POST" | "PUT" | "PATCH", body: unknown): RequestInit => ({
  method,
  headers: { "content-type": "application/json" },
  body: JSON.stringify(body),
});
const post = (body: unknown) => send("POST", body);

/** Who the server takes this browser for. */
export const getMe = () => json<Me>("/api/me");
export const signIn = (username: string, password: string) => json<Me>("/api/login", post({ username, password }));
export async function signOut(): Promise<void> {
  await answered(await call("/api/logout", { method: "POST" }));
}
export async function changePassword(current: string, next: string): Promise<void> {
  await answered(await call("/api/password", post({ current, next })));
}

// For admins: the accounts, what each may do, and which books each may use.
const accountPath = (id: string) => `/api/admin/accounts/${encodeURIComponent(id)}`;
/** The vaults, for an admin: what is in a folder, a note's text, the note a link names, and a note changed. */
const vaultQuery = (path: string) => `?path=${encodeURIComponent(path)}`;
export const listVault = (path: string) =>
  json<{ entries: VaultEntry[] }>(`/api/admin/vaults${vaultQuery(path)}`).then((r) => r.entries);
export const readVaultNote = (path: string) => json<VaultNote>(`/api/admin/vaults/note${vaultQuery(path)}`);
export const findVaultNote = (from: string, name: string) =>
  json<{ path: string | null }>(
    `/api/admin/vaults/find?from=${encodeURIComponent(from)}&name=${encodeURIComponent(name)}`,
  ).then((r) => r.path);
export const writeVaultNote = (note: VaultNote) => json<VaultNote>("/api/admin/vaults/note", send("PUT", note));

/** Invitations: links that let someone make their own account. */
export const listInvites = () => json<{ invites: InviteInfo[] }>("/api/admin/invites").then((r) => r.invites);
/** Make one. The word for its link comes back this once. */
export const createInvite = (invite: NewInviteRequest) =>
  json<{ invite: InviteInfo; code: string }>("/api/admin/invites", post(invite));
export async function revokeInvite(id: string): Promise<void> {
  await answered(await call(`/api/admin/invites/${encodeURIComponent(id)}`, { method: "DELETE" }));
}
/** Whether an invitation's word is still good: asked by someone who has no account yet. */
export const checkInvite = (code: string) =>
  json<{ good: boolean }>(`/api/invites/${encodeURIComponent(code)}`).then((r) => r.good);
/** Take an invitation up: the account is made and signed in. */
/** Take up an invitation. `adult` is whoever takes it saying they are 18 or older; no account is made without it. */
export const redeemInvite = (code: string, username: string, password: string, adult: boolean, terms: string | null) =>
  json<Me>(`/api/invites/${encodeURIComponent(code)}`, post({ username, password, adult, terms }));
/** Say that you are 18 or older, and take the terms of use in force. */
export const agree = (terms: string | null) => json<Me>("/api/me/agree", post({ adult: true, terms }));
export const listAccounts = () => json<{ accounts: AccountAccess[]; books: BookInfo[] }>("/api/admin/accounts");
export const createAccount = (account: NewAccountRequest) => json<AccountAccess>("/api/admin/accounts", post(account));
export const changeAccount = (id: string, patch: AccountPatchRequest) =>
  json<AccountAccess>(accountPath(id), send("PATCH", patch));
export const changeBooks = (id: string, access: BookAccessRequest) =>
  json<AccountAccess>(`${accountPath(id)}/books`, send("PUT", access));

// Problems players report from their games, and an admin's view of them.
export async function reportProblem(gameId: string, report: NewReportRequest): Promise<void> {
  await answered(await call(`/api/games/${encodeURIComponent(gameId)}/reports`, post(report)));
}
export const listReports = (status?: ReportStatus) =>
  json<{ reports: ProblemReport[] }>(`/api/admin/reports${status ? `?status=${status}` : ""}`).then((r) => r.reports);
export const changeReport = (id: string, patch: ReportPatchRequest) =>
  json<ProblemReport>(`/api/admin/reports/${encodeURIComponent(id)}`, send("PATCH", patch));

/** For admins: the tickets of what the server turned away, and marking one looked at. */
export const listConduct = (status?: ConductTicket["status"]) =>
  json<{ tickets: ConductTicket[] }>(`/api/admin/conduct${status ? `?status=${status}` : ""}`).then((r) => r.tickets);
export const reviewConduct = (id: string, review: { status: ConductTicket["status"]; note: string }) =>
  json<ConductTicket>(`/api/admin/conduct/${encodeURIComponent(id)}`, send("PATCH", review));

/** The games in progress, and those deleted that can still be brought back. */
export const listGames = () => json<{ games: GameSummary[]; removed: RemovedGame[] }>("/api/games");
/** The published adventures in the player's books, to start a game from. */
export const listAdventures = (edition: "2014" | "2024") =>
  json<{ adventures: AdventureChoice[] }>(`/api/adventures?edition=${edition}`).then((r) => r.adventures);
/** Delete a game. It is kept a while, and can be brought back until it is erased. */
export async function removeGame(id: string): Promise<void> {
  await answered(await call(`/api/games/${encodeURIComponent(id)}`, { method: "DELETE" }));
}
export async function restoreGame(id: string): Promise<void> {
  await answered(await call(`/api/games/${encodeURIComponent(id)}/restore`, { method: "POST" }));
}
/** The creatures of the player's books: narrowed, sorted, a page at a time. */
export const listCreatures = (query: CreatureListRequest) => {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined) params.set(k, String(v));
  return json<CreatureList>(`/api/library/creatures?${params}`);
};
/** What is being done for the game this browser's account is having made, or null where none is. */
export const getMaking = () => json<{ making: Making | null }>("/api/games/making").then((r) => r.making);
/** Start a game for a saved character, or for one described on the spot. */
export const createGame = (game: StartGameRequest | NewGameRequest) =>
  json<{ id: string }>("/api/games", post(game)).then((r) => r.id);

const characterPath = (id: string) => `/api/characters/${encodeURIComponent(id)}`;
export const listCharacters = () =>
  json<{ characters: CharacterSummary[] }>("/api/characters").then((r) => r.characters);
export const getCharacter = (id: string) => json<CharacterDetail>(characterPath(id));
export const createCharacter = (draft: CharacterDraftRequest) =>
  json<{ id: string }>("/api/characters", post(draft)).then((r) => r.id);
export const updateCharacter = (id: string, draft: CharacterDraftRequest) =>
  json<CharacterDetail>(characterPath(id), send("PUT", draft));
export async function removeCharacter(id: string): Promise<void> {
  await answered(await call(characterPath(id), { method: "DELETE" }));
}
/** Take a character out of the story it is in: the game stays, and the character is free to begin another. */
export async function withdrawCharacter(id: string): Promise<void> {
  await answered(await call(`${characterPath(id)}/withdraw`, { method: "POST" }));
}
/** The sheet a draft would make, or what the rules say is wrong with it. */
/** An account's own pictures of its characters, and whether it may have more drawn today. */
export const listPortraits = () => json<PortraitList>("/api/portraits");
/** Have a picture drawn: it takes the image model the better part of a minute. */
export const drawPortrait = (request: PortraitRequestInput) =>
  json<{ portrait: PortraitInfo; left: number | null }>("/api/portraits", post(request));
/** Send a picture of one's own to be a character's portrait. It is checked before it is kept. */
export const uploadPortrait = (file: File) =>
  json<{ portrait: PortraitInfo; left: number | null }>("/api/portraits/upload", {
    method: "POST",
    headers: { "content-type": file.type },
    body: file,
  });
export async function removePortrait(id: string): Promise<void> {
  await answered(await call(`/api/portraits/${encodeURIComponent(id)}`, { method: "DELETE" }));
}
// The links say which making of the pictures they are to: a browser keeps a picture a week, and those kept
// from before backgrounds were taken away aren't to be shown.
export const portraitUrl = (id: string) => `/api/portraits/${encodeURIComponent(id)}?v=2`;
export const portraitTokenUrl = (id: string) => `/api/portraits/${encodeURIComponent(id)}/token?v=2`;
/** A character's map of the worlds it has travelled in. */
export const getAtlas = (id: string) =>
  json<{ worlds: AtlasWorld[] }>(`/api/characters/${encodeURIComponent(id)}/atlas`);
export const previewCharacter = (draft: CharacterDraftRequest) =>
  json<SheetView>("/api/characters/preview", post(draft));
/** Mark an offline character's sheet: damage, what is spent, notes. */
export const trackCharacter = (id: string, track: TrackRequest) =>
  json<CharacterDetail>(`${characterPath(id)}/track`, post(track));
export const restCharacter = (id: string, kind: "short" | "long") =>
  json<CharacterDetail>(`${characterPath(id)}/rest`, post({ kind }));
/** Search the content library by name; with no `q`, just ask whether there is one. */
export const searchLibrary = (
  kind: "creature" | "spell" | "item",
  edition: "2014" | "2024" = "2024",
  q?: string,
  maxLevel?: number,
  /** Spells on this class's list only; with no `q`, all of them. */
  classId?: string,
) => {
  const query = new URLSearchParams({ kind, edition });
  if (q) query.set("q", q);
  if (classId) query.set("class", classId);
  if (maxLevel !== undefined) query.set("maxLevel", String(maxLevel));
  return json<LibrarySearch>(`/api/library?${query}`);
};

/** The bundled spells a class may choose from at a level, with what each does. */
export const getSpells = (classId: string, edition: "2014" | "2024", level: number) =>
  json<SpellChoices>(`/api/spells?${new URLSearchParams({ class: classId, edition, level: String(level) })}`);

/** One spell by bundled id or library key, with what it does. */
export const getSpell = (key: string, edition: "2014" | "2024") =>
  json<SpellNote>(`/api/spell?${new URLSearchParams({ key, edition })}`);
/** A book's own words for one of its spells: answered only where the server shows them. */
export const getBookSpell = (key: string, edition: "2014" | "2024") =>
  json<SpellNote>(`/api/library/spell?${new URLSearchParams({ key, edition })}`);
/** A creature's stat block from a book: answered only where the server shows its books' text. */
export const getBookCreature = (key: string, edition: "2014" | "2024") =>
  json<StatBlock>(`/api/library/creature?${new URLSearchParams({ key, edition })}`);
/** What an item from a book says, in the book's words: answered only where the server shows them. */
export const getBookItem = (key: string, edition: "2014" | "2024") =>
  json<{ name: string; text: string }>(`/api/library/item?${new URLSearchParams({ key, edition })}`);
/** What a feat from a book says, in the book's words: answered only where the server shows them. */
export const getBookFeat = (key: string, edition: "2014" | "2024") =>
  json<{ name: string; text: string }>(`/api/library/feat?${new URLSearchParams({ key, edition })}`);
/** A book's own words for a species (its traits) or a background (its text): answered only where the server shows them. */
export const getBookOrigin = (key: string, edition: "2014" | "2024") =>
  json<{ name: string; features: FeatureInfo[]; text: string }>(
    `/api/library/origin?${new URLSearchParams({ key, edition })}`,
  );
/** What a subclass from a book gives, level by level, in the book's words. */
export const getBookSubclass = (key: string, classId: string, edition: "2014" | "2024") =>
  json<{ features: FeatureInfo[] }>(
    `/api/library/subclass?${new URLSearchParams({ key, class: classId, edition })}`,
  ).then((r) => r.features);

/** What the bundled rules have to carry: weapons, armour and gear, each with its numbers. */
export const getEquipment = (edition: "2014" | "2024") =>
  json<{ items: EquipmentItem[] }>(`/api/equipment?edition=${edition}`).then((r) => r.items);
export const getRules = (edition: "2014" | "2024") => json<RulesInfo>(`/api/rules?edition=${edition}`);
export const openGame = (id: string) => json<Snapshot>(`/api/games/${encodeURIComponent(id)}`);

/** Split a stream of text chunks into complete lines. */
export function lineSplitter(onLine: (line: string) => void): { push(chunk: string): void; end(): void } {
  let rest = "";
  return {
    push(chunk) {
      rest += chunk;
      let at: number;
      while ((at = rest.indexOf("\n")) >= 0) {
        const line = rest.slice(0, at).trim();
        rest = rest.slice(at + 1);
        if (line) onLine(line);
      }
    },
    end() {
      if (rest.trim()) onLine(rest.trim());
      rest = "";
    },
  };
}

/** Send a command and hand each message to `onMessage` as it arrives. */
/** The word that lets others sit at a game's table, for its host; `anew` makes the old one worthless. */
export const inviteToGame = (id: string, anew = false) =>
  json<{ code: string }>(`/api/games/${encodeURIComponent(id)}/invite${anew ? "?anew=1" : ""}`, post({}));
/** What someone asked to a table is shown before they sit down. */
export const getInvitation = (id: string, code: string) =>
  json<TableInvite>(`/api/games/${encodeURIComponent(id)}/invite/${encodeURIComponent(code)}`);
export async function joinGame(id: string, request: JoinGameRequest): Promise<void> {
  await answered(await call(`/api/games/${encodeURIComponent(id)}/join`, post(request)));
}
/** Get up from a table ("me"), or as its host have an account get up. */
export async function leaveSeat(id: string, account: string): Promise<void> {
  await answered(
    await call(`/api/games/${encodeURIComponent(id)}/seats/${encodeURIComponent(account)}`, { method: "DELETE" }),
  );
}

/** Gather a table for a game to be played with others: the world is written behind it while they sit down. */
export const gatherTable = (start: StartGameRequest) => json<{ id: string; code: string }>("/api/tables", post(start));
const tableUrl = (id: string, code: string | null) =>
  `/api/tables/${encodeURIComponent(id)}${code ? `?code=${encodeURIComponent(code)}` : ""}`;
export const getTable = (id: string, code: string | null) => json<GatheringView>(tableUrl(id, code));
export const sitAtTable = (id: string, seat: { code: string; characterId: string }) =>
  json<GatheringView>(`/api/tables/${encodeURIComponent(id)}/sit`, post(seat));
export async function leaveTable(id: string, code: string | null): Promise<void> {
  await answered(
    await call(`/api/tables/${encodeURIComponent(id)}/seats/me${code ? `?code=${encodeURIComponent(code)}` : ""}`, {
      method: "DELETE",
    }),
  );
}
export const beginTable = (id: string) => json<{ id: string }>(`/api/tables/${encodeURIComponent(id)}/begin`, post({}));

/** For a player coming back to a game: what they missed, or null if they weren't away. */
export const getCatchUp = (id: string) =>
  json<{ catchUp: CatchUp | null }>(`/api/games/${encodeURIComponent(id)}/catch-up`).then((r) => r.catchUp);

/** Ask for the DM to play a character whose player is away: one seat by its character's id, or "all". */
export async function standIn(id: string, seat: string): Promise<void> {
  await answered(await call(`/api/games/${encodeURIComponent(id)}/stand-in`, post({ seat })));
}
/** What a host sets for their table. */
export async function setTable(id: string, settings: { cooldownSeconds: number }): Promise<void> {
  await answered(await call(`/api/games/${encodeURIComponent(id)}/table`, send("PUT", settings)));
}

/**
 * This window's own name, made as the page loads: what it does itself comes back in the command's reply, and
 * everything else (the others at the table, or one's own other window) over the stream.
 */
const WINDOW =
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `w${Date.now()}${Math.random()}`.replace(".", "");

/**
 * Watch a game as the others at its table play it: each message as the
 * watcher's own character sees it. The browser keeps the connection up by
 * itself; `onBack` says it has come back after dropping, when anything may
 * have been missed. Returns how to stop watching.
 */
export function watchGame(
  id: string,
  onMessage: (m: GameWire) => void,
  onBack: () => void,
  onDropped: () => void = () => undefined,
): () => void {
  const source = new EventSource(`/api/games/${encodeURIComponent(id)}/stream?window=${WINDOW}`);
  let dropped = false;
  source.onmessage = (e: MessageEvent<string>) => onMessage(JSON.parse(e.data) as GameWire);
  source.onerror = () => {
    if (!dropped) onDropped();
    dropped = true;
  };
  source.onopen = () => {
    if (dropped) onBack();
    dropped = false;
  };
  return () => source.close();
}

export async function sendCommand(id: string, command: Command, onMessage: (m: GameWire) => void): Promise<void> {
  const request = post(command);
  const res = await answered(
    await call(`/api/games/${encodeURIComponent(id)}/commands`, {
      ...request,
      headers: { ...request.headers, "x-window": WINDOW },
    }),
  );
  if (!res.body) throw new Error(await problemOf(res));
  const lines = lineSplitter((line) => onMessage(JSON.parse(line) as GameWire));
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    lines.push(value);
  }
  lines.end();
}
